/**
 * A Busca Avançada traduzida em condições que o SERVIDOR entende.
 *
 * ## O que isto é, e o que NÃO é
 *
 * Não é mover o filtro para o servidor. A Busca Avançada continua sendo
 * avaliada aqui, em `applyAdvancedSearchFilters`, e continua sendo a palavra
 * final sobre o que a tela mostra.
 *
 * Isto decide o que **baixar**. A listagem carrega os chamados não finalizados
 * mais os 100 finalizados mais recentes; um chamado fechado há meses que casa
 * o filtro nunca chegava na tela, e o operador lia isso como "o filtro não
 * funciona" -- foi o relato de 13/10/2026 sobre o filtro de Serviço. Mandando
 * as condições junto, os 100 que vêm passam a ser os 100 que casam.
 *
 * ## A regra que governa este arquivo
 *
 *   A consulta do servidor tem de ser um SUPERCONJUNTO do que o navegador
 *   aceita. Na dúvida, não mande o campo.
 *
 * Campo traduzido errado para MENOS esconde chamado -- o modo de falha
 * silencioso que este desenho existe para evitar. Campo que fica de fora só
 * faz baixar linha a mais, e o navegador descarta. Por isso a lista pode
 * crescer aos poucos: ela não precisa estar completa para estar correta.
 *
 * ## Quem ficou de fora, e por quê
 *
 * - `client`: aqui o valor é procurado num texto composto (nome exibido do
 *   cliente MAIS `ticket.client`) e casa por nome OU código. A condição do
 *   servidor olha só a coluna `client` -- seria mais estreita.
 * - `ccUsers`: a coluna é `longtext` com JSON dentro; o servidor não tem
 *   predicado para ela.
 * - `product`, `linhaDoProduto`, `representative`: existem no servidor, mas
 *   filtram por `whereHas` na empresa do cliente e nunca foram medidos contra
 *   a tabela inteira de finalizados. Entram quando houver medição.
 * - `assignedTo`, `createdBy`: os marcadores dinâmicos resolvem no servidor
 *   pelo usuário autenticado e aqui por `usuarioAtual`. É a mesma pessoa, mas
 *   a equivalência merece teste próprio antes.
 */

// A MESMA normalização que o modal e o filtro do navegador usam: os três
// precisam concordar sobre o que é "nada selecionado".
import { comoLista } from './selecaoMultipla';
import { ehValorDinamico } from './filtroDinamico';

/** Os operadores do servidor, nomeados para não virarem string solta. */
const IGUAL = 'equals';
const UM_DE = 'in';
const CONTEM = 'contains';
const A_PARTIR_DE = 'greater_than_or_equal';
const ATE = 'less_than_or_equal';

const temValor = (v) => v !== null && v !== undefined && v !== '';

/**
 * A data, deslocada um dia para FORA do intervalo pedido.
 *
 * O servidor compara por dia usando `config('app.timezone')`; o navegador usa
 * o fuso de quem está olhando. Quando os dois diferem, a borda do intervalo
 * anda algumas horas -- e, se andar para dentro, o servidor deixa de baixar um
 * chamado que o navegador aceitaria. Um dia de folga para cada lado custa
 * algumas linhas a mais e elimina a pergunta.
 *
 * Esta base já pagou por essa divergência: `created_today` existe no servidor
 * justamente porque "hoje" calculado no navegador não batia com "hoje" do
 * servidor.
 */
const comFolga = (valor, dias) => {
    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) return null;

    data.setDate(data.getDate() + dias);

    return data.toISOString();
};

/**
 * As condições a mandar, ou `[]` quando não há busca avançada.
 *
 * @param {object|null} filtros  o estado do modal de Busca Avançada
 * @returns {Array<{field_name: string, operator: string, value: *}>}
 */
export const condicoesDaBuscaAvancada = (filtros) => {
    if (!filtros || typeof filtros !== 'object') return [];

    const condicoes = [];
    const põe = (field_name, operator, value) => {
        if (!temValor(value)) return;
        condicoes.push({ field_name, operator, value });
    };

    põe('title', CONTEM, filtros.title);
    // O marcador "__status_nao_concluido__" vai cru: o servidor o resolve em
    // `FilterService::aplicarValorDinamico`, com a MESMA constante.
    põe('status_id', IGUAL, filtros.status);
    /*
     * Prioridade, categoria e canal aceitam mais de uma opção (ponto 11 da
     * Gisele). Com uma só, continua `equals` -- é o caso comum, e manter o
     * mesmo payload evita estrear um caminho novo onde o antigo funciona.
     * Com várias, `in`, que o servidor resolve em `whereIn` pela MESMA rotina
     * do filtro salvo (`FilterService::applyCondition`).
     *
     * A regra do arquivo continua valendo: o servidor tem de ser um
     * SUPERCONJUNTO do navegador. `in` com a lista inteira é exatamente o
     * conjunto que o navegador aceita -- nem mais estreito, nem mais largo.
     */
    const põeSelecao = (field_name, valor) => {
        const escolhidos = comoLista(valor);

        if (escolhidos.length === 0) return;
        if (escolhidos.length === 1) {
            põe(field_name, IGUAL, escolhidos[0]);
            return;
        }

        condicoes.push({ field_name, operator: UM_DE, value: escolhidos });
    };

    põeSelecao('priority', filtros.priority);
    põeSelecao('category', filtros.category);
    põe('contact', CONTEM, filtros.contact);
    põeSelecao('channel', filtros.channel);
    põe('ultima_acao', IGUAL, filtros.ultimaAcao);
    /*
     * O TEXTO escrito dentro de uma ação.
     *
     * Este campo inverte a regra do arquivo, e é o único: ele NÃO tem metade
     * no navegador. A listagem traz o chamado, não a conversa dele, então o
     * navegador não consegue avaliar a condição -- e por isso
     * `applyAdvancedSearchFilters` a ignora de propósito.
     *
     * A consequência é que aqui o servidor é a palavra final, e não um
     * superconjunto. Isso só é seguro porque a tela, ao ver este campo
     * preenchido, passa a montar a lista a partir do RESULTADO do servidor
     * (o mesmo caminho da caixa de pesquisa) em vez dos ativos que já tem em
     * memória. Sem essa troca, os chamados ativos apareceriam todos, sem
     * passar por filtro nenhum.
     */
    põe('acao', CONTEM, filtros.textoNaAcao);
    /*
     * Serviço também aceita mais de uma opção, e o `in` é seguro aqui: quem
     * resolve essa condição no servidor é `FilterService::aplicarCondicaoDeServico`,
     * que trata `in` pelo MESMO caminho do `equals` -- passa por
     * `idsDeServicoDaCondicao`, que expande a subárvore de CADA id escolhido.
     * A consulta continua sendo um superconjunto do que o navegador aceita,
     * que é a regra deste arquivo.
     */
    põeSelecao('service', filtros.serviceId);
    /*
     * O departamento aceita mais de uma opção, e o marcador dinâmico pode
     * estar no meio da lista.
     *
     * Com uma escolha só, o marcador vai cru e o servidor o resolve
     * (`FilterService::aplicarValorDinamico`). Com VÁRIAS, ele não resolve:
     * aquele método compara o valor INTEIRO da condição com o marcador, e um
     * `in` com a lista não casa. O `whereIn` sairia com o texto do marcador
     * dentro, que o MySQL converte para 0 -- e aí a consulta ficaria mais
     * ESTREITA que o navegador, escondendo chamado. Nesse caso não mandamos
     * nada: baixar demais a tela corrige, baixar de menos não.
     */
    const grupos = comoLista(filtros.serviceGroupId);
    if (grupos.length === 1 || !grupos.some((g) => ehValorDinamico(g))) {
        põeSelecao('service_group_id', grupos);
    }

    [
        ['created_at', filtros.createdAtStart, filtros.createdAtEnd],
        ['resolved_at', filtros.resolvedAtStart, filtros.resolvedAtEnd],
        ['updated_at', filtros.updatedAtStart, filtros.updatedAtEnd],
    ].forEach(([campo, de, ate]) => {
        if (temValor(de)) põe(campo, A_PARTIR_DE, comFolga(de, -1));
        if (temValor(ate)) põe(campo, ATE, comFolga(ate, 1));
    });

    return condicoes;
};

/**
 * Os campos da Busca Avançada que este arquivo NÃO traduz.
 *
 * Nomeados aqui, e não só no comentário, porque `totalVemDoServidor` precisa
 * saber quais são — e duas listas que podem divergir seria o mesmo erro que a
 * regra do superconjunto existe para evitar.
 */
const CAMPOS_SO_NO_NAVEGADOR = [
    'client',
    'ccUsers',
    'product',
    'linhaDoProduto',
    'representative',
    'assignedTo',
    'createdBy',
];

/**
 * O total que o servidor conta é EXATO para esta busca?
 *
 * Só quando todo campo preenchido foi traduzido. Se algum ficou no navegador,
 * a consulta do servidor é mais ampla de propósito -- e aí o número dele
 * contaria chamados que a tela vai descartar. Mostrar isso no cabeçalho
 * trocaria um número que engana para menos ("de 5", quando há milhares) por um
 * que engana para mais, e o segundo é mais difícil de desconfiar.
 *
 * @param {object|null} filtros
 */
export const totalVemDoServidor = (filtros) => {
    if (!filtros || typeof filtros !== 'object') return false;

    /*
     * `comoLista`, e não `temValor`: desde que Cliente aceita mais de um, o
     * campo vazio é uma LISTA VAZIA -- e `[]` é truthy. Com `temValor`, abrir
     * a busca avançada e mexer no cliente sem deixar nenhum marcado faria o
     * total voltar a ser estimado para sempre, sem nada preenchido na tela.
     */
    if (CAMPOS_SO_NO_NAVEGADOR.some((campo) => comoLista(filtros[campo]).length > 0)) return false;

    // Sem nenhuma condição traduzida não há o que o servidor recortar, e quem
    // responde é o caminho de sempre (sem busca avançada).
    return condicoesDaBuscaAvancada(filtros).length > 0;
};
